// Runtime theme (colour preset) switching for the agent UI.
//
// Cards stay WHITE and the page stays light in every preset. What recolours is the "chrome"
// (the navy header bar, dial-pad panel, chat panels, nav dropdown, call FAB, table header
// bands), the "accent" (buttons, links, active states, selected rows), and the canvas — which
// takes a 4% tint of the chrome so the white cards have something to sit on. The styling half
// lives in src/assets/scss/_theme.scss, selected by a `data-cx-theme` attribute on <html>.
//
// WHY A FIXED LIST AND NOT A COLOUR PICKER. 107 places in the JSX carry a literal
// className="text-white", which Bootstrap compiles to rgba(var(--bs-white-rgb), …). No runtime
// custom property can reach a literal class name, so on-chrome text is permanently #FFFFFF and
// every preset's chrome MUST clear WCAG AA (4.5:1) against white. Of 14 colours a non-designer
// would plausibly pick, 8 fail that — a free picker would let someone ship an unreadable header.
//
// All twelve presets are pre-validated, stop by stop, interiors included. The tightest in the
// palette is Teal's gradient end at 5.47:1. BEFORE ADDING ONE: check every stop AND the accent
// against white, and add every new stop to PRESET_RE and CHANNEL_RE in scripts/check-theme.sh —
// a preset whose stops are missing from those is a preset the guard cannot protect.

// CRA inlines process.env at build time, so this is a per-BUILD switch, not a per-tenant one:
// the dev server has to be restarted after changing it, a hot reload will not pick it up, and
// one build cannot have it on for one customer and off for another. Per-tenant control would
// need a column on the branding record instead.
export const IS_THEME_PICKER_ON = process.env.REACT_APP_THEME_PICKER === 'true';

export const THEME_STORAGE_KEY = 'callex-theme';

// `id` is the data-cx-theme attribute value.
// `chrome` is stop 1 — used ONLY for the mobile address-bar tint in applyTheme().
// `swatch` is what the drawer chip paints: a flat colour for a solid preset, the full gradient
// for the others. Both are duplicated from _theme.scss, which remains the source of truth —
// nothing else in the app should read them, and if the two drift the swatch lies about the preset.
//
// Three groups, rendered as three sections in the drawer:
//   solid     one flat colour, --cx-chrome-gradient: none
//   gradient  two stops
//   multi     three stops
// Every stop, and every point between stops, clears WCAG AA 4.5:1 against white — see the note
// in _theme.scss for the measured figures and why checking the stops is provably sufficient.
// The tightest in the palette is Teal's gradient end at 5.47:1.
export const THEME_GROUPS = [
  { id: 'solid', label: 'Solid' },
  { id: 'gradient', label: 'Gradient' },
  { id: 'multi', label: 'Multi-colour' }
];

export const THEME_PRESETS = [
  { id: 'classic', label: 'Classic', group: 'solid', chrome: '#122335', swatch: '#122335' },
  { id: 'ocean', label: 'Ocean', group: 'solid', chrome: '#0a3d62', swatch: '#0a3d62' },
  { id: 'forest', label: 'Forest', group: 'solid', chrome: '#14432a', swatch: '#14432a' },
  { id: 'wine', label: 'Wine', group: 'solid', chrome: '#7a1c2b', swatch: '#7a1c2b' },
  { id: 'deepsea', label: 'Deep Sea', group: 'gradient', chrome: '#0a3d62', swatch: 'linear-gradient(135deg, #0a3d62 0%, #12628f 100%)' },
  { id: 'teal', label: 'Teal', group: 'gradient', chrome: '#0e4a44', swatch: 'linear-gradient(135deg, #0e4a44 0%, #0f766e 100%)' },
  { id: 'royal', label: 'Royal', group: 'gradient', chrome: '#3b1e6e', swatch: 'linear-gradient(135deg, #3b1e6e 0%, #5b2e9e 100%)' },
  { id: 'sunset', label: 'Sunset', group: 'gradient', chrome: '#7a1c2b', swatch: 'linear-gradient(135deg, #7a1c2b 0%, #a62639 100%)' },
  { id: 'midnight', label: 'Midnight', group: 'multi', chrome: '#1a1a4e', swatch: 'linear-gradient(135deg, #1a1a4e 0%, #3d1a6b 50%, #5c1a5c 100%)' },
  { id: 'aurora', label: 'Aurora', group: 'multi', chrome: '#0d4f4a', swatch: 'linear-gradient(135deg, #0d4f4a 0%, #123a6b 50%, #3a1f6e 100%)' },
  { id: 'jade', label: 'Jade', group: 'multi', chrome: '#0f3d2e', swatch: 'linear-gradient(135deg, #0f3d2e 0%, #125049 50%, #0e5a6b 100%)' },
  { id: 'ember', label: 'Ember', group: 'multi', chrome: '#5c1420', swatch: 'linear-gradient(135deg, #5c1420 0%, #7a2d12 50%, #7a4a0e 100%)' },
];

export const DEFAULT_THEME = 'classic';

const isValidTheme = (id) => THEME_PRESETS.some((p) => p.id === id);

// Reads can throw, not just return null — Safari private mode and browsers configured to block
// site data raise on access rather than returning empty. Never let that take down the app.
export const readTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isValidTheme(stored) ? stored : DEFAULT_THEME;
  } catch (e) {
    return DEFAULT_THEME;
  }
};

// Classic is the :root default, so it carries no attribute — an unknown or corrupted value then
// degrades to Classic rather than to an unstyled app.
export const applyTheme = (id) => {
  const theme = isValidTheme(id) ? id : DEFAULT_THEME;
  const root = document.documentElement;
  if (theme === DEFAULT_THEME) {
    root.removeAttribute('data-cx-theme');
  } else {
    root.setAttribute('data-cx-theme', theme);
  }
  // Keep the Android address-bar tint in step with the bar it sits above.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const preset = THEME_PRESETS.find((p) => p.id === theme);
    if (preset) meta.setAttribute('content', preset.chrome);
  }
  return theme;
};

export const writeTheme = (id) => {
  const theme = applyTheme(id);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (e) {
    // Preference is lost on reload but the current session still renders correctly.
  }
  return theme;
};

// Logout wipes localStorage in four places (axiosApi.js, Header.jsx, MainLayout.jsx,
// CallingSidebar.jsx). A bare clear() takes the theme with it, so the agent picks Teal at 9am
// and logs in tomorrow to a white-and-navy app and reports it as a bug. Use this instead.
export const clearStorageKeepTheme = () => {
  let theme = null;
  try {
    theme = localStorage.getItem(THEME_STORAGE_KEY);
  } catch (e) {
    theme = null;
  }
  try {
    localStorage.clear();
    if (theme) localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (e) {
    // Nothing useful to do — the session is ending either way.
  }
};
